import { supabase } from '../lib/supabase';

export type SeedingMethod = 'random' | 'score_based' | 'manual';
export type BracketType = 'single_elimination' | 'double_elimination';

export interface TournamentEntry {
  id: string;
  user_id: string;
  tournament_id: string;
  bracket_seed?: number;
}

export interface BracketMatch {
  round_number: number;
  match_number: number;
  participant1_id?: string;
  participant2_id?: string;
  participant1_entry_id?: string;
  participant2_entry_id?: string;
}

export class BracketGenerationService {
  static async generateBracket(
    tournamentId: string,
    seedingMethod: SeedingMethod = 'random',
    lockAndActivate: boolean = false
  ): Promise<void> {
    try {
      const { data: tournament, error: tournamentError } = await supabase
        .from('tournaments')
        .select('id, title, registration_locked')
        .eq('id', tournamentId)
        .single();

      if (tournamentError || !tournament) {
        throw new Error('Tournament not found');
      }

      const { data: entries, error: entriesError } = await supabase
        .from('tournament_entries')
        .select('id, user_id, tournament_id, score')
        .eq('tournament_id', tournamentId)
        .in('status', ['registered', 'evaluated']);

      if (entriesError || !entries || entries.length === 0) {
        throw new Error('No registered participants found');
      }

      const participantCount = entries.length;
      const bracketSize = this.getNextPowerOfTwo(participantCount);
      const totalRounds = Math.log2(bracketSize);

      console.log(`Generating bracket: ${participantCount} participants, ${bracketSize} slots, ${totalRounds} rounds`);

      const seededEntries = await this.seedParticipants(entries, seedingMethod);

      await this.updateEntriesWithSeeds(seededEntries);

      const matches = this.generateMatches(seededEntries, bracketSize, totalRounds);

      await this.saveMatches(tournamentId, matches);

      await this.saveBracketConfig(tournamentId, bracketSize, totalRounds, seedingMethod);

      if (lockAndActivate && !tournament.registration_locked) {
        const { error: lockError } = await supabase
          .from('tournaments')
          .update({
            registration_locked: true,
            locked_participant_count: bracketSize,
            locked_at: new Date().toISOString(),
            status: 'active'
          })
          .eq('id', tournamentId);

        if (lockError) {
          console.error('Error locking registration:', lockError);
          throw new Error('Failed to lock registration and activate tournament');
        }
      } else if (lockAndActivate) {
        const { error: activateError } = await supabase
          .from('tournaments')
          .update({ status: 'active' })
          .eq('id', tournamentId);

        if (activateError) {
          console.error('Error activating tournament:', activateError);
        }
      }

      if (lockAndActivate || tournament.registration_locked) {
        await supabase.rpc('set_round1_deadlines', {
          tournament_id_param: tournamentId
        });
      }

      console.log('Bracket generation completed successfully');
    } catch (error) {
      console.error('Error generating bracket:', error);
      throw error;
    }
  }

  private static getNextPowerOfTwo(n: number): number {
    return Math.pow(2, Math.ceil(Math.log2(n)));
  }

  private static async seedParticipants(
    entries: any[],
    seedingMethod: SeedingMethod
  ): Promise<TournamentEntry[]> {
    let seededEntries: TournamentEntry[] = [...entries];

    switch (seedingMethod) {
      case 'random':
        seededEntries = this.shuffleArray(seededEntries);
        break;

      case 'score_based':
        seededEntries = await this.seedByScore(seededEntries);
        break;

      case 'manual':
        seededEntries = seededEntries.sort((a, b) => {
          const seedA = a.bracket_seed || 999999;
          const seedB = b.bracket_seed || 999999;
          return seedA - seedB;
        });
        break;
    }

    return seededEntries.map((entry, index) => ({
      ...entry,
      bracket_seed: index + 1
    }));
  }

  private static shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  private static async seedByScore(entries: TournamentEntry[]): Promise<TournamentEntry[]> {
    const entriesWithScores = await Promise.all(
      entries.map(async (entry) => {
        const { data: performances } = await supabase
          .from('performances')
          .select('score')
          .eq('user_id', entry.user_id)
          .order('score', { ascending: false })
          .limit(1);

        const highestScore = performances?.[0]?.score || 0;

        return {
          ...entry,
          highestScore
        };
      })
    );

    return entriesWithScores.sort((a, b) => b.highestScore - a.highestScore);
  }

  private static async updateEntriesWithSeeds(seededEntries: TournamentEntry[]): Promise<void> {
    const updates = seededEntries.map(entry => ({
      id: entry.id,
      bracket_seed: entry.bracket_seed,
      current_round: 1
    }));

    for (const update of updates) {
      await supabase
        .from('tournament_entries')
        .update({
          bracket_seed: update.bracket_seed,
          current_round: update.current_round
        })
        .eq('id', update.id);
    }
  }

  private static generateMatches(
    seededEntries: TournamentEntry[],
    bracketSize: number,
    totalRounds: number
  ): BracketMatch[] {
    const matches: BracketMatch[] = [];
    const matchesPerRound = bracketSize / 2;

    for (let round = 1; round <= totalRounds; round++) {
      const matchesInRound = matchesPerRound / Math.pow(2, round - 1);

      for (let matchNum = 1; matchNum <= matchesInRound; matchNum++) {
        const match: BracketMatch = {
          round_number: round,
          match_number: matchNum
        };

        if (round === 1) {
          const slot1 = (matchNum - 1) * 2;
          const slot2 = slot1 + 1;

          const standardPairing = this.getStandardPairing(matchNum, bracketSize);
          const seed1 = standardPairing[0];
          const seed2 = standardPairing[1];

          const entry1 = seededEntries.find(e => e.bracket_seed === seed1);
          const entry2 = seededEntries.find(e => e.bracket_seed === seed2);

          if (entry1) {
            match.participant1_id = entry1.user_id;
            match.participant1_entry_id = entry1.id;
          }

          if (entry2) {
            match.participant2_id = entry2.user_id;
            match.participant2_entry_id = entry2.id;
          }
        }

        matches.push(match);
      }
    }

    this.linkMatchesToNextRound(matches);

    return matches;
  }

  private static getStandardPairing(matchNumber: number, bracketSize: number): [number, number] {
    const pairings: Record<number, [number, number][]> = {
      4: [[1, 4], [2, 3]],
      8: [[1, 8], [4, 5], [2, 7], [3, 6]],
      16: [[1, 16], [8, 9], [4, 13], [5, 12], [2, 15], [7, 10], [3, 14], [6, 11]],
      32: [
        [1, 32], [16, 17], [8, 25], [9, 24], [4, 29], [13, 20], [5, 28], [12, 21],
        [2, 31], [15, 18], [7, 26], [10, 23], [3, 30], [14, 19], [6, 27], [11, 22]
      ],
      64: [
        [1, 64], [32, 33], [16, 49], [17, 48], [8, 57], [25, 40], [9, 56], [24, 41],
        [4, 61], [29, 36], [13, 52], [20, 45], [5, 60], [28, 37], [12, 53], [21, 44],
        [2, 63], [31, 34], [15, 50], [18, 47], [7, 58], [26, 39], [10, 55], [23, 42],
        [3, 62], [30, 35], [14, 51], [19, 46], [6, 59], [27, 38], [11, 54], [22, 43]
      ]
    };

    const bracketPairings = pairings[bracketSize] || [];
    return bracketPairings[matchNumber - 1] || [matchNumber * 2 - 1, matchNumber * 2];
  }

  private static linkMatchesToNextRound(matches: BracketMatch[]): void {
    const matchesByRound: Record<number, BracketMatch[]> = {};

    matches.forEach(match => {
      if (!matchesByRound[match.round_number]) {
        matchesByRound[match.round_number] = [];
      }
      matchesByRound[match.round_number].push(match);
    });

    Object.keys(matchesByRound).forEach(roundStr => {
      const round = parseInt(roundStr);
      const currentRoundMatches = matchesByRound[round];
      const nextRoundMatches = matchesByRound[round + 1];

      if (nextRoundMatches) {
        currentRoundMatches.forEach((match, index) => {
          const nextMatchIndex = Math.floor(index / 2);
          (match as any).next_match_number = nextRoundMatches[nextMatchIndex]?.match_number;
          (match as any).next_round_number = round + 1;
        });
      }
    });
  }

  private static async saveMatches(tournamentId: string, matches: BracketMatch[]): Promise<void> {
    const matchesByRound: Record<number, any[]> = {};

    matches.forEach(match => {
      if (!matchesByRound[match.round_number]) {
        matchesByRound[match.round_number] = [];
      }
      matchesByRound[match.round_number].push(match);
    });

    const savedMatches: Record<string, string> = {};

    for (let round = Math.max(...Object.keys(matchesByRound).map(Number)); round >= 1; round--) {
      const roundMatches = matchesByRound[round];

      for (const match of roundMatches) {
        const nextMatchKey = match.next_round_number && match.next_match_number
          ? `${match.next_round_number}-${match.next_match_number}`
          : null;

        const isBye = match.participant1_id && !match.participant2_id;

        const { data: savedMatch, error } = await supabase
          .from('tournament_matches')
          .insert({
            tournament_id: tournamentId,
            round_number: match.round_number,
            match_number: match.match_number,
            participant1_id: match.participant1_id || null,
            participant2_id: match.participant2_id || null,
            participant1_entry_id: match.participant1_entry_id || null,
            participant2_entry_id: match.participant2_entry_id || null,
            next_match_id: nextMatchKey ? savedMatches[nextMatchKey] : null,
            match_status: isBye ? 'completed' : 'pending',
            winner_id: isBye ? match.participant1_id : null,
            completed_at: isBye ? new Date().toISOString() : null
          })
          .select('id')
          .single();

        if (error) {
          console.error('Error saving match:', error);
          throw error;
        }

        const matchKey = `${match.round_number}-${match.match_number}`;
        savedMatches[matchKey] = savedMatch.id;

        if (isBye) {
          await this.handleByeAdvancement(
            match.participant1_id!,
            match.participant1_entry_id!,
            match.round_number,
            nextMatchKey ? savedMatches[nextMatchKey] : null
          );
        }
      }
    }
  }

  private static async handleByeAdvancement(
    participantId: string,
    entryId: string,
    currentRound: number,
    nextMatchId: string | null
  ): Promise<void> {
    const nextRound = currentRound + 1;

    await supabase
      .from('tournament_entries')
      .update({ current_round: nextRound })
      .eq('id', entryId);

    if (nextMatchId) {
      const { data: nextMatch } = await supabase
        .from('tournament_matches')
        .select('participant1_id, participant2_id')
        .eq('id', nextMatchId)
        .single();

      if (nextMatch) {
        if (!nextMatch.participant1_id) {
          await supabase
            .from('tournament_matches')
            .update({
              participant1_id: participantId,
              participant1_entry_id: entryId
            })
            .eq('id', nextMatchId);
        } else if (!nextMatch.participant2_id) {
          await supabase
            .from('tournament_matches')
            .update({
              participant2_id: participantId,
              participant2_entry_id: entryId
            })
            .eq('id', nextMatchId);
        }
      }
    }

    console.log(`Participant ${participantId} advanced from round ${currentRound} via bye`);
  }

  private static async saveBracketConfig(
    tournamentId: string,
    bracketSize: number,
    totalRounds: number,
    seedingMethod: SeedingMethod
  ): Promise<void> {
    const { error } = await supabase
      .from('bracket_progression')
      .insert({
        tournament_id: tournamentId,
        total_rounds: totalRounds,
        bracket_size: bracketSize,
        bracket_type: 'single_elimination',
        seeding_method: seedingMethod,
        generated_at: new Date().toISOString()
      });

    if (error) {
      console.error('Error saving bracket config:', error);
      throw error;
    }
  }

  static async regenerateBracket(tournamentId: string, seedingMethod: SeedingMethod, lockAndActivate: boolean = false): Promise<void> {
    await supabase.from('tournament_matches').delete().eq('tournament_id', tournamentId);
    await supabase.from('bracket_progression').delete().eq('tournament_id', tournamentId);

    await supabase
      .from('tournament_entries')
      .update({
        bracket_seed: null,
        current_round: 1,
        eliminated_at: null
      })
      .eq('tournament_id', tournamentId);

    await this.generateBracket(tournamentId, seedingMethod, lockAndActivate);
  }

  static async autoRegenerateBracket(tournamentId: string): Promise<void> {
    try {
      const { data: tournament, error: tournamentError } = await supabase
        .from('tournaments')
        .select('id, registration_locked')
        .eq('id', tournamentId)
        .single();

      if (tournamentError || !tournament) {
        return;
      }

      if (tournament.registration_locked) {
        return;
      }

      const { data: entries } = await supabase
        .from('tournament_entries')
        .select('id')
        .eq('tournament_id', tournamentId)
        .in('status', ['registered', 'evaluated']);

      if (!entries || entries.length < 2) {
        return;
      }

      await this.regenerateBracket(tournamentId, 'random', false);
    } catch (error) {
      console.error('Error auto-regenerating bracket:', error);
    }
  }
}
